import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router';
import {
    Button,
    Chip,
    CircularProgress,
    Link,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    Typography
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {findSimilarPitches} from '../../../../utils/api/similarPitches';
import {formatTimestamp} from '../../../../utils/formatTimestamp';
import SendMessageButton from '../../../../components/SendMessageButton';
import {useSession} from '../../../../firebase/UserProvider';
import {getExpertByUid} from '../../../experts/expertsData';
import UserAvatar from '../../../../components/UserAvatar';

const MAX_OVERLAP_CHIPS_PER_GROUP = 8;

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

const getDisplayName = (u) => {
    if (!u) return 'Unknown';
    if (u.displayName) return u.displayName;
    if (u.name) return u.name;
    const first = u.firstName ? capitalize(u.firstName) : '';
    const last = u.lastName ? capitalize(u.lastName) : '';
    const combined = `${first} ${last}`.trim();
    return combined || u.email || u.uid || u.id || 'Unknown';
};

const getAuthorUid = (author) => author?.uid || author?.id || null;

const getExpertHandle = (author) => {
    const authorUid = getAuthorUid(author);
    if (authorUid) {
        const byUid = getExpertByUid(authorUid);
        if (byUid?.handle) return byUid.handle;
    }
    return '';
};

const formatUpdatedAt = (value) => formatTimestamp(value, 'Unknown date');

const normalizeOverlapValues = (values) => {
    if (!Array.isArray(values)) return [];
    return values.filter(value => typeof value === 'string' && value.trim().length > 0);
};

const OverlapGroup = ({label, values}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const normalized = normalizeOverlapValues(values);
    if (normalized.length === 0) return null;

    const hasOverflow = normalized.length > MAX_OVERLAP_CHIPS_PER_GROUP;
    const visible = isExpanded ? normalized : normalized.slice(0, MAX_OVERLAP_CHIPS_PER_GROUP);
    const hiddenCount = normalized.length - MAX_OVERLAP_CHIPS_PER_GROUP;

    return (
        <Stack spacing={0.75}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {visible.map(value => (
                    <Chip key={`${label}-${value}`} size="small" variant="outlined" label={value}/>
                ))}
                {hasOverflow && (
                    <Chip
                        size="small"
                        variant="text"
                        clickable
                        onClick={() => setIsExpanded(prev => !prev)}
                        label={isExpanded ? 'Show less' : `+${hiddenCount} more`}
                    />
                )}
            </Stack>
        </Stack>
    );
};

const SimilarPitchCard = ({item, currentUserId, onAuthorClick}) => {
    const title = item?.reportPreview?.title || item?.uid || 'Untitled pitch';
    const topicSummary = item?.reportPreview?.summary;
    const pitchHref = item?.uid ? `/products/${item.uid}` : null;
    const authors = Array.isArray(item?.authors) ? item.authors : [];

    return (
        <Paper variant="outlined" sx={{p: 1.5}}>
            <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                    {pitchHref ? (
                        <Link
                            href={pitchHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            color="inherit"
                            sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0}}
                        >
                            <Typography variant="body1" fontWeight={600}
                                        sx={{wordBreak: 'break-word'}}>{title}</Typography>
                            <OpenInNewIcon fontSize="inherit"/>
                        </Link>
                    ) : (
                        <Typography variant="body1" fontWeight={600}>{title}</Typography>
                    )}
                    <Chip size="small" label={`#${item?.rank ?? '-'}`}/>
                </Stack>

                {/*<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>*/}
                {/*    <Chip size="small" label={`Similarity: ${Math.round((item?.similarity || 0) * 100)}%`}/>*/}
                {/*    <Chip size="small" label={item?.reportPreview?.status || 'Unknown status'}/>*/}
                {/*</Stack>*/}

                {topicSummary && (
                    <Stack spacing={1}>

                        <Typography variant="body2" color="text.secondary">
                            Extracted Summary:
                        </Typography>

                        <Typography variant="body1" color="text.primary">
                            {topicSummary}
                        </Typography>
                    </Stack>
                )}

                {(item?.topicOverlap?.entitiesCount > 0 || item?.topicOverlap?.eventsCount > 0 || item?.topicOverlap?.geoCount > 0) && (
                    <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">Topic overlap:</Typography>
                        <OverlapGroup label="Entities" values={item?.topicOverlap?.entities}/>
                        <OverlapGroup label="Events" values={item?.topicOverlap?.events}/>
                        <OverlapGroup label="Geography" values={item?.topicOverlap?.geo}/>
                    </Stack>
                )}

                <Typography variant="caption" color="text.secondary">
                    Updated: {formatUpdatedAt(item?.reportPreview?.updatedAt)}
                </Typography>

                {authors.length > 0 && (
                    <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">Authors:</Typography>
                        <List disablePadding>
                            {authors.map((author, idx) => {
                                const authorUid = getAuthorUid(author);
                                const id = authorUid || author?.email || `${item?.uid || 'pitch'}-author-${idx}`;
                                const displayName = getDisplayName(author);
                                const handle = getExpertHandle(author);
                                const canOpenExpert = Boolean(handle);
                                const shouldShowMessageAction = Boolean(authorUid) && authorUid !== currentUserId;

                                return (
                                    <ListItem
                                        key={id}
                                        alignItems="flex-start"
                                        disablePadding
                                        secondaryAction={
                                            shouldShowMessageAction ? (
                                                <SendMessageButton
                                                    isIcon={true}
                                                    user={{
                                                        userDisplayName: displayName,
                                                        displayName,
                                                        headshot: author?.headshot,
                                                        agency: author?.agency,
                                                        office: author?.office,
                                                        poc: author?.poc,
                                                        contact: author?.contact,
                                                        title: author?.userTitle,
                                                        email: author?.email,
                                                        uid: authorUid,
                                                        id: authorUid,
                                                    }}
                                                />
                                            ) : null
                                        }
                                    >
                                        <ListItemButton
                                            disabled={!canOpenExpert}
                                            onClick={() => onAuthorClick(handle)}
                                        >
                                            <ListItemAvatar>
                                                <UserAvatar
                                                    user={author}
                                                    userId={authorUid}
                                                    sx={{width: 40, height: 40}}
                                                />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={displayName}
                                                secondary="Analyst"
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
};

const SimilarPitchesSection = ({isActive}) => {
    const {reportId, uid} = useParams();
    const {user: authedUser} = useSession() || {};
    const sourceUid = uid || reportId;
    const currentUserId = authedUser?.uid || authedUser?.id || null;

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const hasSourceUid = useMemo(() => Boolean(sourceUid), [sourceUid]);

    const loadSimilarPitches = useCallback(async () => {
        if (!hasSourceUid) {
            setItems([]);
            setError('Missing pitch uid in route params.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const result = await findSimilarPitches(sourceUid);
            setItems(result);
        } catch (err) {
            setItems([]);
            setError(err?.message || 'Failed to load similar pitches');
        } finally {
            setLoading(false);
        }
    }, [hasSourceUid, sourceUid]);

    useEffect(() => {
        if (isActive) {
            loadSimilarPitches();
        }
    }, [isActive, loadSimilarPitches]);

    const handleAuthorClick = useCallback((handle) => {
        if (!handle) return;
        const expertUrl = `/experts/${encodeURIComponent(handle)}`;
        window.open(expertUrl, '_blank', 'noopener,noreferrer');
    }, []);

    return (
        <Stack p={2} spacing={1.5} sx={{height: '100%', overflowY: 'auto', boxSizing: 'border-box'}}>
            <Typography variant="body2" color="text.secondary">
                Other pitches you can access with overlapping Wikidata entities (or similar titles).
            </Typography>

            {loading && (
                <Stack py={2} alignItems="center" spacing={1}>
                    <CircularProgress size={24}/>
                    <Typography variant="caption" color="text.secondary">Finding similar pitches...</Typography>
                </Stack>
            )}

            {!loading && error && (
                <Stack spacing={1}>
                    <Typography variant="body2" color="error">{error}</Typography>
                    <Button size="small" variant="outlined" onClick={loadSimilarPitches}>Retry</Button>
                </Stack>
            )}

            {!loading && !error && items.length === 0 && (
                <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">No similar pitches found.</Typography>
                    <Button size="small" variant="outlined" onClick={loadSimilarPitches}>Retry</Button>
                </Stack>
            )}

            {!loading && !error && items.length > 0 && (
                <Stack spacing={1.5}>
                    {items.map(item => (
                        <SimilarPitchCard
                            key={item.uid}
                            item={item}
                            currentUserId={currentUserId}
                            onAuthorClick={handleAuthorClick}
                        />
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

export default SimilarPitchesSection;

